import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronRight, ListCollapse, ListTree, Search } from 'lucide-react';
import { Button, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { TypeBadge, orgTypeLabel } from './TypeBadge.js';

export { orgTypeLabel };

export interface OrgNode {
  id: string;
  code: string;
  /**
   * Qisqa belgi — "PCH-12", "MTU-5", stansiya kodi.
   *
   * Daraxtda ko'rsatiladigan YAGONA identifikator shu. `code` esa
   * ko'rsatilmaydi: qisqa belgisi yo'q korxonalarning kodi nomning o'zidan
   * yasalgan ("BUXORO-LOGISTIKA-MARKAZI") va uni nom yonida takrorlash
   * nomni siqib, "Buxoro..." qilib qo'yardi. Kodning to'lig'i o'ng
   * paneldagi kartochkada turadi.
   */
  shortName?: string | null;
  name: string;
  type: string;
  children: OrgNode[];
}

/** Ochiq tugunlarni tepadan pastga yassilaydi — klaviatura shu tartibda yuradi. */
const flatten = (nodes: OrgNode[], collapsed: Set<string>, out: OrgNode[] = []): OrgNode[] => {
  for (const node of nodes) {
    out.push(node);
    if (node.children.length > 0 && !collapsed.has(node.id)) {
      flatten(node.children, collapsed, out);
    }
  }
  return out;
};

/** Har bir tugun uchun otasini topadi — `←` bilan yuqoriga qaytish uchun. */
const parentMap = (nodes: OrgNode[], parent: string | null, out = new Map<string, string | null>()) => {
  for (const node of nodes) {
    out.set(node.id, parent);
    parentMap(node.children, node.id, out);
  }
  return out;
};

/** Bolasi bor tugunlarning barcha identifikatorlari — "hammasini yig'ish" uchun. */
const branchIds = (nodes: OrgNode[], out: string[] = []): string[] => {
  for (const node of nodes) {
    if (node.children.length > 0) {
      out.push(node.id);
      branchIds(node.children, out);
    }
  }
  return out;
};

const hit = (node: OrgNode, needle: string): boolean =>
  node.name.toLowerCase().includes(needle) ||
  (node.shortName ?? '').toLowerCase().includes(needle);

/**
 * Qidiruv daraxtni QISQARTIRADI, yashirmaydi.
 *
 * Mos kelgan tugunning otalari saqlanadi — aks holda natija kontekstsiz
 * qolardi va "PCh-5 qaysi MTU ostida?" degan savolga javob yo'qolardi.
 * Tugunning O'ZI mos kelsa, bolalari to'lig'icha qoladi: nomi bo'yicha
 * topilgan MTU ichiga kirib ko'rish mumkin bo'lsin.
 */
const filterTree = (nodes: OrgNode[], needle: string): OrgNode[] =>
  nodes.flatMap((node) => {
    if (hit(node, needle)) return [node];
    const children = filterTree(node.children, needle);
    return children.length > 0 ? [{ ...node, children }] : [];
  });

/** Mos kelgan qismni belgilaydi — ko'z uni qidirib o'tirmaydi. */
const Highlight = ({ text, needle }: { text: string; needle: string }): ReactNode => {
  if (needle === '') return text;
  const at = text.toLowerCase().indexOf(needle);
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-[2px] bg-accent-soft text-text">
        {text.slice(at, at + needle.length)}
      </mark>
      {text.slice(at + needle.length)}
    </>
  );
};

export interface OrgTreeProps {
  nodes: OrgNode[];
  onSelect: (node: OrgNode) => void;
  selectedId?: string | null;
}

/**
 * Tashkiliy tuzilma daraxti.
 *
 * Klaviatura bilan to'liq boshqariladi (`↑↓` yurish, `→` ochish, `←` yopish,
 * `Enter` tanlash) va WAI-ARIA daraxt naqshiga amal qiladi: bitta `tabindex=0`
 * daraxt bo'ylab ko'chib yuradi, qolgan tugunlar `tabindex=-1` da qoladi —
 * shu tufayli `Tab` daraxtdan bir bosishda chiqib ketadi.
 *
 * Holat **yopilgan** tugunlar bo'yicha saqlanadi, ochilganlar bo'yicha emas:
 * yangi ma'lumot kelganda daraxt o'z-o'zidan ochiq bo'ladi va odam qidirib
 * o'tirmaydi.
 */
export const OrgTree = ({ nodes, onSelect, selectedId = null }: OrgTreeProps) => {
  const rootRef = useRef<HTMLUListElement>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const needle = query.trim().toLowerCase();
  const searching = needle !== '';

  const shown = useMemo(
    () => (searching ? filterTree(nodes, needle) : nodes),
    [nodes, needle, searching],
  );

  /* Qidiruv paytida yig'ilgan holat E'TIBORGA OLINMAYDI: natijaning otasi
     yopiq bo'lsa, topilgan tugun ko'rinmay qolardi — ya'ni qidiruv "hech
     narsa topilmadi" degandek tuyulardi. */
  const effectiveCollapsed = useMemo(
    () => (searching ? new Set<string>() : collapsed),
    [searching, collapsed],
  );

  const visible = useMemo(
    () => flatten(shown, effectiveCollapsed),
    [shown, effectiveCollapsed],
  );
  const parents = useMemo(() => parentMap(shown, null), [shown]);
  const byId = useMemo(() => new Map(visible.map((node) => [node.id, node])), [visible]);

  const branches = useMemo(() => branchIds(nodes), [nodes]);
  const allCollapsed = branches.length > 0 && branches.every((id) => collapsed.has(id));

  const activeId = focusedId ?? visible[0]?.id ?? null;

  const toggle = (id: string): void => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const focusNode = (id: string | undefined): void => {
    if (id === undefined) return;
    setFocusedId(id);
    rootRef.current?.querySelector<HTMLElement>(`[data-node-id="${id}"]`)?.focus();
  };

  /**
   * Joriy tugun DOM dan o'qiladi (holatdan emas): fokus dastur tashqarisidan
   * ham qo'yilishi mumkin va shunda ham klaviatura to'g'ri joydan davom etadi.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    const item = (event.target as HTMLElement).closest<HTMLElement>('[data-node-id]');
    const id = item?.dataset.nodeId;
    if (id === undefined) return;

    const node = byId.get(id);
    if (node === undefined) return;

    const index = visible.findIndex((candidate) => candidate.id === id);
    const hasChildren = node.children.length > 0;
    const isOpen = hasChildren && !effectiveCollapsed.has(id);

    switch (event.key) {
      case 'ArrowDown':
        focusNode(visible[index + 1]?.id);
        break;
      case 'ArrowUp':
        focusNode(visible[index - 1]?.id);
        break;
      case 'ArrowRight':
        if (hasChildren && !isOpen) toggle(id);
        else if (isOpen) focusNode(node.children[0]?.id);
        break;
      case 'ArrowLeft':
        if (isOpen) toggle(id);
        else focusNode(parents.get(id) ?? undefined);
        break;
      case 'Home':
        focusNode(visible[0]?.id);
        break;
      case 'End':
        focusNode(visible[visible.length - 1]?.id);
        break;
      case 'Enter':
      case ' ':
        onSelect(node);
        break;
      default:
        return;
    }

    event.preventDefault();
  };

  const renderNodes = (items: OrgNode[]) =>
    items.map((node) => {
      const hasChildren = node.children.length > 0;
      const open = hasChildren && !effectiveCollapsed.has(node.id);
      const selected = node.id === selectedId;

      return (
        <li
          key={node.id}
          role="treeitem"
          data-node-id={node.id}
          aria-expanded={hasChildren ? open : undefined}
          aria-selected={selected}
          aria-label={`${node.name}, ${orgTypeLabel(node.type)}`}
          tabIndex={node.id === activeId ? 0 : -1}
          onFocus={() => setFocusedId(node.id)}
          className="rounded-[var(--radius-sm)]"
        >
          <div
            className={cn(
              'flex h-9 items-center gap-2 rounded-[var(--radius-sm)] pr-3 pl-1',
              'transition-[background-color,color] duration-[var(--motion-fast)]',
              'ease-[var(--ease-standard)]',
              selected ? 'bg-accent-soft shadow-[var(--shadow-rail)]' : 'hover:bg-surface-2',
            )}
          >
            {hasChildren ? (
              <button
                type="button"
                tabIndex={-1}
                aria-label={t(open ? 'org.treeCollapseNode' : 'org.treeExpandNode', {
                  name: node.name,
                })}
                onClick={() => toggle(node.id)}
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center',
                  'rounded-[var(--radius-sm)] text-text-faint hover:bg-surface-2 hover:text-text',
                )}
              >
                <ChevronRight
                  size={16}
                  aria-hidden
                  className={cn(
                    'transition-transform duration-[var(--motion-fast)]',
                    'ease-[var(--ease-standard)]',
                    open ? 'rotate-90' : undefined,
                  )}
                />
              </button>
            ) : (
              <span aria-hidden className="size-6 shrink-0" />
            )}

            <button
              type="button"
              tabIndex={-1}
              onClick={() => onSelect(node)}
              className="flex min-w-0 flex-1 items-center gap-2 text-left"
            >
              {/* `title` MAJBURIY: chuqur darajada nom kesiladi va bu uni
                  o'qishning yagona yo'li. Ilgari kesilgan nomni ochishning
                  hech qanday usuli yo'q edi. */}
              <span
                title={node.name}
                className={cn(
                  'min-w-0 flex-1 truncate text-sm',
                  selected ? 'font-medium text-primary' : 'text-text',
                )}
              >
                <Highlight text={node.name} needle={needle} />
              </span>
              {node.shortName === null || node.shortName === undefined ? null : (
                /* Kod ham `truncate`: uzun qisqa belgi (ba'zan 12 belgigacha)
                   `shrink-0` bo'lgani uchun nomni siqib qo'yardi. Endi u ham
                   o'z chegarasida kesiladi va to'lig'i `title` da qoladi. */
                <span
                  title={node.shortName}
                  className="tabular max-w-[6rem] shrink-0 truncate text-xs text-text-muted"
                >
                  <Highlight text={node.shortName} needle={needle} />
                </span>
              )}
            </button>

            {/* Nishon hech qachon siqilmaydi — aks holda u kod ustiga chiqib
                ketardi va ikkalasi ham o'qilmay qolardi. */}
            <TypeBadge type={node.type} />
          </div>

          {open ? (
            // Har daraja 16px chapga suriladi va vertikal chiziq bilan
            // bog'lanadi — relslar kabi: qaysi tugun kimning ostida turgani
            // ko'rinib turadi.
            <div className="motion-tree-in grid">
              <div className="overflow-hidden">
                <ul role="group" className="ml-4 border-l border-border pl-1">
                  {renderNodes(node.children)}
                </ul>
              </div>
            </div>
          ) : null}
        </li>
      );
    });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Qidiruv YOPISHIB turadi: 500 dan ortiq birlikli daraxtda aylantirib
          ketgach, qidiruv maydoniga qaytish uchun tepaga chiqish kerak
          bo'lardi. */}
      <div className="sticky top-0 z-[var(--z-sticky)] flex items-center gap-2 border-b border-border bg-surface p-3">
        <div className="relative flex min-w-0 flex-1 items-center">
          <Search
            size={16}
            aria-hidden
            className="pointer-events-none absolute left-3 text-text-faint"
          />
          {/* `Input` EMAS: spetsifikatsiya bu maydonga `--bg-subtle` to'ldirma
              beradi, `Input` esa doim oq sirtda turadi. Bitta joy uchun
              komponentga variant qo'shish uni chalkashtirardi. */}
          <input
            type="search"
            aria-label={t('org.treeSearch')}
            placeholder={t('org.treeSearch')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className={cn(
              'h-10 w-full min-w-0 rounded-[var(--radius-sm)] border border-border-control',
              'bg-surface-2 pr-3 pl-9 text-sm text-text placeholder:text-text-faint',
              'transition-[border-color] duration-[var(--motion-fast)]',
              'ease-[var(--ease-standard)] focus-visible:border-accent',
            )}
          />
        </div>

        <Button
          variant="ghost"
          size="sm"
          aria-label={t(allCollapsed ? 'org.treeExpandAll' : 'org.treeCollapseAll')}
          title={t(allCollapsed ? 'org.treeExpandAll' : 'org.treeCollapseAll')}
          disabled={searching || branches.length === 0}
          onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(branches))}
          className="shrink-0 px-2"
        >
          {allCollapsed ? (
            <ListTree size={16} aria-hidden />
          ) : (
            <ListCollapse size={16} aria-hidden />
          )}
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {visible.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-text-muted">
            {t('org.treeSearchEmpty')}
          </p>
        ) : (
          <ul
            ref={rootRef}
            role="tree"
            aria-label={t('org.treeLabel')}
            onKeyDown={handleKeyDown}
            className="flex flex-col gap-0.5"
          >
            {renderNodes(shown)}
          </ul>
        )}
      </div>
    </div>
  );
};
